// Lib
import { useEffect } from 'react';

// Utils
import { getTableCellEditorId, getTableGridEditorId } from '../utils/tableEditorSessionUtils';

/**
 * Custom hook for managing table selection side effects
 *
 * @param {Object} props - Hook configuration
 */
export const useTableSelectionEffects = ({
    elementId,
    currentlyEditingEditorId,
    hotTableInstanceRef,
    isSingleSelected,
    isResizing,
    isReadOnly,
}) => {
    useEffect(() => {
        // Prevent cells from reselecting if another element is selected or if user is editing title/caption
        // this is just because on first click onto another element the table
        // rerenders before meta is cleared from app selection state

        if (!hotTableInstanceRef.current || isResizing) return;

        if (!isSingleSelected) {
            hotTableInstanceRef.current.deselectCell();
            hotTableInstanceRef.current.unlisten();
        }

        // Start listening when the table is single selected so that handsontable's shortcuts work
        if (isSingleSelected && !isReadOnly) hotTableInstanceRef.current.listen();
    }, [isSingleSelected]);

    /**
     * If user focuses on another editor key on the table (e.g. title/caption), deselect the current cell selection
     */
    useEffect(() => {
        if (!hotTableInstanceRef.current || !hotTableInstanceRef.current.isListening()) return;

        const isEditingCellOrGrid =
            !currentlyEditingEditorId ||
            currentlyEditingEditorId === getTableGridEditorId(elementId) ||
            currentlyEditingEditorId === getTableCellEditorId(elementId);

        if (isEditingCellOrGrid) return;

        hotTableInstanceRef.current.deselectCell();
        hotTableInstanceRef.current.unlisten();
    }, [elementId, currentlyEditingEditorId]);
};

export default useTableSelectionEffects;
